import { datadogLogs } from "@datadog/browser-logs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { toast } from "react-toastify";
import { isFacebookSDKLoadedAtom } from "../atoms/isFacebookSDKLoadedAtom";
import { QUERY_KEYS } from "../constants/queryKeys";
import { FacebookErrorResponse } from "../utils/facebookUtils";

interface FacebookProfileRequestParamsProps {
  fields: string;
  edges?: string;
}

export interface FacebookProfileResponseProps {
  id: string;
  name?: string;
  email?: string;
  accounts?: {
    data: {
      id: string;
      name?: string;
      promotion_eligible?: boolean;
    }[];
  };
}

export const useGetFacebookProfile = (
  params: FacebookProfileRequestParamsProps,
  enabled = false,
) => {
  const isFacebookSDKLoaded = useAtomValue(isFacebookSDKLoadedAtom);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QUERY_KEYS.GET_FACEBOOK_PROFILE, params],
    queryFn: async () => {
      try {
        const res = await getFacebookProfile(params);
        return res;
      } catch (e) {
        const error = e as FacebookErrorResponse["error"];
        datadogLogs.logger.error("Error fetching facebook profile", {
          params,
          error,
        });
        toast.error(
          "Something went wrong while fetching your Facebook profile. Please try again later!",
        );
        if (error.type === "OAuthException") {
          queryClient.setQueriesData(
            { queryKey: [QUERY_KEYS.GET_FACEBOOK_LOGIN_STATUS] },
            () => null,
          );
        }
        return Promise.reject(error);
      }
    },
    enabled: enabled && isFacebookSDKLoaded ? undefined : false,
  });
};

const getFacebookProfile = (
  params: FacebookProfileRequestParamsProps,
): Promise<FacebookProfileResponseProps> => {
  return new Promise((res, rej) => {
    FB.api(
      "/me",
      params,
      (response: FacebookErrorResponse | FacebookProfileResponseProps) => {
        if ("error" in response) {
          return rej(response.error);
        } else {
          return res(response);
        }
      },
    );
  });
};
